import { Group } from "@visx/group";
import { Circle } from "@visx/shape";
import {
  DependencyList,
  memo,
  MouseEvent,
  ReactNode,
  useId,
  useMemo,
} from "react";
import {
  BORDER_RADIUS,
  BUBBLE_HEIGHT,
  MARKERTEXT_WIDTH,
} from "shared/components/dataMarker/markerConstants";
import {
  buildPointerPath,
  useMarkerGeometry,
} from "shared/components/dataMarker/useMarkerGeometry";
import { useMeasureElementWidth } from "shared/components/dataMarker/useMeasureElementWidth";
import { Flex } from "shared/components/Spor";

interface DataMarkerProps {
  cx: number;
  cy: number;
  xMax: number;
  badgeY: number;
  outlineColor: string;
  lineColor: string;
  markerConfig?: {
    verticalLineStrokeWidth?: number;
    pointRadius?: number;
    pointStrokeWidth?: number;
  };
  bubbleContent: ReactNode;
  labelContent?: ReactNode;
  ariaLabel?: string;
  measureDeps?: DependencyList;
  onMouseOver?: (event: MouseEvent<SVGElement>) => void;
  onMouseMove?: (event: MouseEvent<SVGElement>) => void;
  onMouseLeave?: () => void;
}

export const DataMarker = memo(
  ({
    cx,
    cy,
    xMax,
    badgeY,
    outlineColor,
    lineColor,
    markerConfig,
    bubbleContent,
    labelContent,
    ariaLabel,
    measureDeps = [],
    onMouseOver,
    onMouseMove,
    onMouseLeave,
  }: DataMarkerProps) => {
    const id = useId();
    const shadowFilterId = useMemo(
      () => `data-marker-shadow-${id.replace(/:/g, "")}`,
      [id],
    );

    const verticalLineStrokeWidth = markerConfig?.verticalLineStrokeWidth ?? 3;
    const pointRadius = markerConfig?.pointRadius ?? 6;
    const pointStrokeWidth = markerConfig?.pointStrokeWidth ?? 5;

    const { ref: contentRef, measuredWidth } =
      useMeasureElementWidth<HTMLDivElement>(measureDeps);

    const geometry = useMarkerGeometry({
      cx,
      cy,
      contentWidth: measuredWidth,
      chartWidth: xMax,
    });

    const pointerPath = useMemo(() => buildPointerPath(geometry), [geometry]);

    return (
      <>
        <defs>
          <filter
            id={shadowFilterId}
            x="-20%"
            y="-20%"
            width="140%"
            height="140%"
            colorInterpolationFilters="sRGB"
          >
            <feDropShadow
              dx="0"
              dy="2"
              stdDeviation="3"
              floodColor="rgba(0, 0, 0, 0.25)"
            />
          </filter>
        </defs>
        <line
          x1={cx}
          y1={cy}
          x2={cx}
          y2={badgeY}
          stroke={lineColor}
          strokeWidth={verticalLineStrokeWidth}
        />
        <Circle
          cx={cx}
          cy={cy}
          r={pointRadius}
          fill={lineColor}
          stroke={outlineColor}
          strokeWidth={pointStrokeWidth}
          onMouseOver={onMouseOver}
          onMouseMove={onMouseMove}
          onMouseLeave={onMouseLeave}
          style={{ cursor: "pointer" }}
        />

        <Group
          role="group"
          aria-label={ariaLabel ?? "Marker"}
          filter={`url(#${shadowFilterId})`}
        >
          <rect
            x={geometry.bubbleLeft}
            y={geometry.bubbleTop}
            width={measuredWidth}
            height={BUBBLE_HEIGHT}
            rx={BORDER_RADIUS}
            ry={BORDER_RADIUS}
            fill={outlineColor}
          />
          <path d={pointerPath} fill={outlineColor} />
          <foreignObject
            x={geometry.bubbleLeft}
            y={geometry.bubbleTop}
            height={BUBBLE_HEIGHT}
            width={measuredWidth}
          >
            <Flex
              flexDirection="row"
              alignItems="center"
              justifyContent="center"
              height="100%"
              width="fit-content"
              paddingX={1}
              ref={contentRef}
            >
              {bubbleContent}
            </Flex>
          </foreignObject>
          {labelContent ? (
            <foreignObject
              x={cx - MARKERTEXT_WIDTH / 2}
              y={badgeY}
              width={MARKERTEXT_WIDTH}
              height={50}
            >
              {labelContent}
            </foreignObject>
          ) : null}
        </Group>
      </>
    );
  },
);

DataMarker.displayName = "DataMarker";
